import React, { useState } from "react";
import CustomTable from "./../../../../../../common/CustomTable";
import Loader from "./../../../../../../common/Loader";
import Dropdown from "./../../../../../../common/Dropdown";
import { Tooltip } from "react-tooltip";
import { truncate } from "lodash";
import VendorAvatar from "components/common/VendorAvatar";
import moment from "moment";
import { formatMoney } from "helpers";
import EmptyStateComp from "components/common/EmptyStateComp";
import { useNavigate } from "react-router-dom";
import CustomTableLoader from "components/common/CustomTableLoader";
import millify from "millify";
import { displayProductChannelIcon } from "helpers/displayProductChannelIcon";
import { useAppDispatch } from "hooks/storeHooks";
import { dashboardActions } from "redux/Ldb/actions";
import { calculateStockoutDate } from "utils/calculate-stockoutdate";
import clsx from "clsx";
import { getSalesChannelIconRaw } from "utils/get-sales-channel-icon-raw";

type VariantTableProps = {
    starredFilter: string;
    productListLoading: boolean;
    data: Record<string, any>[];
    pagination: {
        currentPage: string;
        noOfPages: string;
    };
    setGetProductId: (id: string) => void;
    handleInventoryCheckboxClick: (data: any) => void;
    selectedInventories: any[];
    handleSelectAllInventory: (data: any) => void;
    setSingleArchive: (id: string) => void;
    singleArchive: string;
    setArchiveProductsModal: (value: boolean) => void;
    setSelectedInventories: (data: any) => void;
    selectedInvPeriod: string;
};
const VariantTable = ({
    starredFilter,
    productListLoading,
    data,
    pagination,
    setGetProductId,
    handleInventoryCheckboxClick,
    selectedInventories,
    handleSelectAllInventory,
    setSingleArchive,
    setArchiveProductsModal,
    setSelectedInventories,
    singleArchive,
    selectedInvPeriod,
}: VariantTableProps) => {
    const [expandedRowIdx, setExpandedRowIdx] = useState<number>(-1);
    const [componentList, setComponentList] = useState<any[]>([]);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const tableHeader = [
        {
            title: "Product name",
            widthClass: "w-[300px]",
            onSelectAll: () => {
                handleSelectAllInventory(selectedInventories?.length > 0);
            },
            isAllChecked: selectedInventories?.length === data?.length,
        },
        {
            title: "Categories",
            widthClass: "w-[158px]",
            tooltipIcon: "categories",
            tooltipContent:
                "Groups or classifications that organize products such as Finished goods, Components, and Work-in-Progress (WIP), helping you manage your inventory efficiently.",
        },
        {
            title: "Channel",
            widthClass: "w-[120px]",
            // tooltipIcon: "channel",
            // tooltipContent:
            //     "Groups or classifications that organize products such as Finished goods, Components, and Work-in-Progress (WIP), helping you manage your inventory efficiently.",
        },
        {
            title: "Current stock level",
            widthClass: "w-[100px]",
            tooltipIcon: "current-stock-level",
            tooltipContent:
                "The actual quantity of the product currently available in stock for sale or use at this moment.",
        },
        {
            title: "Forecasted needs",
            widthClass: "w-[158px]",
            tooltipIcon: "forecasted-sales",
            tooltipContent:
                "The estimated amount of the product required to meet anticipated demand over your selected forecast window (between 1 and 6 months)",
        },
        {
            title: "On order",
            widthClass: "w-[100px]",
            tooltipIcon: "on-order",
            tooltipContent:
                "The quantity of this product that has been ordered from suppliers but has not yet been received (incoming inventory)",
        },
        {
            title: "Re-order before",
            widthClass: "w-[158px]",
            tooltipIcon: "stockout-date",
            tooltipContent:
                "The latest date you should place a purchase order to avoid stockouts, based on lead time and forecasted needs. If the required date is within 7 days, we show “Immediately” & “the today's date” to signal urgency.",
        },
        {
            title: "Forecasted stockout date",
            widthClass: "w-[158px]",
            tooltipIcon: "forecasted-stockout-date",
            tooltipContent:
                "This is the projected date when this product is expected to run out of stock based on current inventory levels and demand trends. Use it to stay ahead with timely reorders.",
        },
        {
            title: "",
            widthClass: "w-[56px]",
        },
    ];

    const handlePinInventory = (inventoryId: string) => {
        dispatch(dashboardActions.pinForecastConfig(inventoryId));
    };

    const handleUnpinInventory = (inventoryId: string) => {
        dispatch(dashboardActions.unpinForecastConfig(inventoryId));
    };

    const tableBody = data?.map((product, idx) => {
        const givenDate = moment(product?.forcastData?.stockoutDate);
        const today = moment().startOf("day");
        let differenceInDays = 0;

        if (givenDate.isValid()) {
            differenceInDays = givenDate.diff(today, "days");
        }

        const isChecked =
            [...selectedInventories].some((inventory) => inventory._id === product?._id) ||
            singleArchive === product?._id;

        const isPinned = product?.isPinned;

        return [
            {
                content: (
                    <div
                        className={`py-4 pr-3 flex font-rocGroteskMedium items-center h-full text-sm text-g-75 `}
                    >
                        <div className="flex items-center space-x-3">
                            {product?.productImageDetails?.productAvatar ||
                            product?.productImageDetails?.productImages?.[0] ? (
                                <VendorAvatar
                                    imageSrc={
                                        product?.productImageDetails?.productAvatar ||
                                        product?.productImageDetails?.productImages?.[0]
                                    }
                                    size={32}
                                    name={product?.productName}
                                    containerClassname="!rounded-md"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                    <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                </div>
                            )}
                            <div>
                                <div>
                                    <a id={`product-column-${idx}`}>
                                        <span className="block text-sm">
                                            {truncate(product?.productName, {
                                                length: 30,
                                            })}
                                        </span>
                                        <span className="text-slate-500 text-sm">
                                            {product?.sku ? `SKU-${product?.sku}` : ""}
                                        </span>
                                    </a>
                                    <Tooltip
                                        anchorSelect={`#product-column-${idx}`}
                                        place={"right"}
                                        content={product?.productName}
                                        style={{
                                            maxWidth: "250px",
                                            zIndex: 9999,
                                            background: "#fff",
                                            borderRadius: "6px",
                                            border: "1px solid #E2E8F0",
                                            color: "#1E293B",
                                            boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ),

                expandedRowAction: () => {
                    idx === expandedRowIdx ? setExpandedRowIdx(-1) : setExpandedRowIdx(idx);
                    setComponentList(product?.bomInventory);
                },

                cellClickAction: () => {
                    navigate(`/dashboard/inventory/products/${product?._id}`, {
                        state: { internalNavigation: true },
                    });
                },
                isItemChecked: isChecked,
                onCheckBoxClick: () => {
                    handleInventoryCheckboxClick(product);
                },
                checkboxEndAdornment: (
                    <i
                        className={`ri-star-fill text-lg ${
                            isPinned ? "text-[#FCD34D]" : "text-[#E2E8F0]"
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                            e.stopPropagation();
                            isPinned
                                ? handleUnpinInventory(product?._id)
                                : handlePinInventory(product?._id);
                        }}
                    ></i>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        <span>{product?.inventoryCategory || "Not Available"}</span>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        {product?.channels?.length === 0 ? (
                            "Not available"
                        ) : (
                            <div>
                                <div className="flex items-centers ">
                                    {product?.channels?.map((channel, index) => (
                                        <div
                                            className={clsx(
                                                "rounded-full bg-white w-[26px] h-[26px] flex items-center justify-center border border-slate-200 p-[3px] shadow-lg",
                                                product?.channels?.length !== index + 1 &&
                                                    `translate-x-[${(index + 1) * 5}px]`
                                            )}
                                        >
                                            {(() => {
                                                const icon = getSalesChannelIconRaw(channel);
                                                if (typeof icon === "string") {
                                                    return <img src={icon} alt="" />;
                                                }
                                                return icon;
                                            })()}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-slate-800 `}>
                        <span>
                            {product?.productStockDetails?.stockLevel?.toLocaleString() || "0"}
                            {` `}
                            {product?.productStockDetails?.unitOfMeasurement || ""}
                        </span>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-slate-800 `}>
                        <div className="flex items-center space-x-1.5">
                            {Boolean(product?.forcastData?.forecastedNeeds) && (
                                <div>
                                    <a id={`forecast-needs-${idx}`}>
                                        <span className="material-symbols-outlined text-[14px] text-r-50">
                                            warning
                                        </span>
                                    </a>

                                    <Tooltip
                                        anchorSelect={`#forecast-needs-${idx}`}
                                        place={"top"}
                                        content={
                                            Number(product?.productStockDetails?.stockLevel) < 1
                                                ? "Product is out of stock"
                                                : "Stock for this product may soon run out."
                                        }
                                        style={{ width: "180px", zIndex: 9999 }}
                                    />
                                </div>
                            )}
                            {product?.forcastData?.forecastedNeeds ? (
                                <span className="text-[#FF3D1D]">
                                    {millify(product?.forcastData?.forecastedNeeds, {
                                        precision: 2,
                                    })}{" "}
                                    {product?.productStockDetails?.unitOfMeasurement || ""}
                                </span>
                            ) : !product?.productStockDetails?.stockLevel ||
                              product?.productStockDetails?.stockLevel === 0 ? (
                                <span className="text-[#FF3D1D]">Stockout</span>
                            ) : (
                                <span className="text-[#56B648]">Sufficient</span>
                            )}
                        </div>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-slate-800 `}>
                        <span>{millify(product?.onOrder, { precision: 2 })}</span>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-slate-800 `}>
                        <span>
                            {(() => {
                                const reorderDate = calculateStockoutDate(
                                    product?.forcastData?.stockoutDate,
                                    product?.leadTime
                                );
                                const differenceInDaysInReorderDate = reorderDate?.diff(
                                    today,
                                    "days"
                                );
                                return reorderDate ? (
                                    <div className="flex flex-col gap-1">
                                        <span
                                            className={clsx(
                                                differenceInDaysInReorderDate > 0
                                                    ? "text-slate-500"
                                                    : "text-[#E4281D]"
                                            )}
                                        >
                                            {differenceInDaysInReorderDate === 0
                                                ? "Today"
                                                : formatMoney().format(
                                                      differenceInDaysInReorderDate
                                                  ) + " days"}
                                        </span>
                                        <span>{reorderDate.format("MMM DD, YYYY")}</span>
                                    </div>
                                ) : (
                                    "Not Available"
                                );
                            })()}
                        </span>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-slate-800 `}>
                        <div>
                            <span className="text-sm font-rocGroteskMedium">
                                {!product?.forcastData?.incidentType ? (
                                    "Not Available"
                                ) : product?.forcastData?.incidentType?.toLowerCase() !==
                                  "stockout" ? (
                                    <span className=""> Overstocked </span>
                                ) : //  : product?.forcastData?.incidentType?.toLowerCase() ===
                                //       "stockout" ||
                                differenceInDays > 0 && differenceInDays <= 7 ? (
                                    <>
                                        <span className="text-[#E4281D] block"> Immediately </span>
                                        <span className={` text-slate-500 block`}>
                                            {moment().format("MMM DD, YYYY")}
                                        </span>
                                    </>
                                ) : differenceInDays < 0 ? (
                                    "Not Available"
                                ) : (
                                    <>
                                        <span
                                            className={`block ${
                                                differenceInDays > 7
                                                    ? "text-[#56B648]"
                                                    : "text-[#FF3D1D]"
                                            }`}
                                        >
                                            {differenceInDays === 0
                                                ? "Today"
                                                : formatMoney().format(differenceInDays) + " days"}
                                        </span>
                                        <span className={`block text-slate-500`}>
                                            {moment(product?.forcastData?.stockoutDate).format(
                                                "MMM DD, YYYY"
                                            )}
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },

            {
                content: (
                    <div className={`w-full !pl-4 py-2`} onClick={(e) => e.stopPropagation()}>
                        <Dropdown
                            dropdown={<i className="ri-more-2-fill text-lg text-slate-500"></i>}
                            dropdownClassName={"!w-full !mb-10 !-mt-7"}
                            dropdownContainerClasses={`shadow-cardShadow-2 z-[1400] border-0 rounded !max-h-fit right-[0px] right-[40px]`}
                            name={"dropdown"}
                            dropdownItemsClasses="!bg-transparent "
                            dropdownOptions={[
                                {
                                    label: "View product",
                                    value: "View product",
                                    action: () => {
                                        navigate(`/dashboard/inventory/products/${product?._id}`, {
                                            state: { internalNavigation: true },
                                        });
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                                {
                                    label: "Restock product",
                                    value: "Restock product",
                                    action: () => {
                                        setGetProductId(product?._id);
                                        navigate(
                                            `/dashboard/purchase/create-purchase-request?inventory=${product?._id}`,
                                            { state: { internalNavigation: true } }
                                        );
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                                {
                                    label: "Adjust stock",
                                    value: "Adjust stock",
                                    action: () => {
                                        navigate(
                                            `/dashboard/inventory/stock-control/stock-adjustment?currTab=0&inventoryId=${product?._id}&warehouseId=${product?.productStockSettings[0]?.warehouseId}`
                                        );
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                                {
                                    label: "Archive product",
                                    value: "Archive product",
                                    action: () => {
                                        setSelectedInventories([]);
                                        setArchiveProductsModal(true);
                                        setSingleArchive(product?._id);
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                            ]}
                        />
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200 !border-b",
            },
        ];
    });

    const expandedTableHeader = [
        {
            title: "Component",
            widthClass: "!border-slate-200 w-[180px]",
            tooltipIcon: "component",
            tooltipContent:
                "These are products that typically make up the production of other products (finished goods). They are also known as recipes or ingredients and can be tracked individually.",
        },
        {
            title: "Current stock level",
            widthClass: "!border-slate-200 w-[150px]",
            tooltipIcon: "current-stock-level-2",
            tooltipContent:
                "The actual quantity of the product currently available in stock for sale or use at this moment.",
        },
        {
            title: "Forecasted qty to be used",
            widthClass: "!border-slate-200 w-[200px]",
            tooltipIcon: "forecasted-qty-2",
            tooltipContent:
                "The projected quantity of this component expected to be used across all SKUs it's part of—not just a single finished good based on future demand trends and planned production activities.",
        },
        {
            title: "Forecasted needs",
            widthClass: "!border-slate-200 w-[200px]",
            tooltipIcon: "forecasted-needs",
            tooltipContent:
                "The estimated amount of the product required to meet anticipated demand over your selected forecast window (between 1 and 6 months)",
        },
        {
            title: "Current lead time",
            widthClass: "!border-slate-200 w-[130px]",
            tooltipIcon: "current-lead-time",
            tooltipContent:
                "The average time it takes for a supplier to deliver the product after an order is placed, typically measured in days.",
        },
        {
            title: "Forecasted cost",
            widthClass: "!border-slate-200 w-[172px]",
            tooltipIcon: "forecasted-cost",
            tooltipContent:
                "The predicted total cost of acquiring the forecasted quantity needed, factoring in product unit current price and forecasted demanded quantity.",
        },
        {
            title: "Re-order before",
            widthClass: "!border-slate-200 w-[158px]",
            tooltipIcon: "re-order-before",
            tooltipContent:
                "The latest date you should place a purchase order to avoid stockouts, based on lead time and forecasted needs. If the required date is within 7 days, we show “Immediately” & “the today's date” to signal urgency.",
        },
        { title: "", widthClass: "!border-slate-200 w-[56px]" },
    ];

    const expandedTableBody = data?.[expandedRowIdx]?.bomInventory?.map((comp, idx) => {
        const givenDate = moment(comp?.forecastData?.stockoutDate);
        const today = moment().startOf("day");
        let differenceInDays = 0;

        if (givenDate.isValid()) {
            differenceInDays = givenDate.diff(today, "days");
        }
        return [
            {
                content: (
                    <div
                        className={`py-2 px-3 flex font-rocGroteskMedium items-center h-full text-sm `}
                    >
                        <div className="flex items-center space-x-3">
                            {comp?.productImageDetails?.productImage?.[0] ? (
                                <VendorAvatar
                                    imageSrc={comp?.productImageDetails?.productImage?.[0]}
                                    size={32}
                                    name={comp?.productName}
                                    containerClassname="!rounded-md"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                    <i className="ri-box-2-fill text-[20px] text-slate-500"></i>
                                </div>
                            )}
                            <div>
                                <div>
                                    <a id={`product-column-${idx}`}>
                                        <span className="block text-sm">
                                            {truncate(comp?.productName, {
                                                length: 20,
                                            })}
                                        </span>
                                    </a>
                                    <Tooltip
                                        anchorSelect={`#product-column-${idx}`}
                                        place={"right"}
                                        content={comp?.productName}
                                        style={{
                                            maxWidth: "250px",
                                            zIndex: 9999,
                                            background: "#fff",
                                            borderRadius: "6px",
                                            border: "1px solid #E2E8F0",
                                            color: "#1E293B",
                                            boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ),

                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
                cellClickAction: () => {
                    navigate(`/dashboard/inventory/products/${comp?._id}`, {
                        state: { internalNavigation: true },
                    });
                },
            },

            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        <p className="">
                            {millify(comp?.productStockDetails?.stockLevel || 0, {
                                precision: 2,
                            }) || 0}{" "}
                            {comp?.productStockDetails?.unitOfMeasurement || ""}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium border-slate-200 text-sm text-g-75 `}
                    >
                        <span className="">
                            {comp?.forecastData?.forecast ? (
                                <span className="">
                                    {millify(comp?.forecastData?.forecast, { precision: 2 })}{" "}
                                    {comp?.productStockDetails?.unitOfMeasurement || ""}
                                </span>
                            ) : (
                                "Not Available"
                            )}
                        </span>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        <p>
                            {comp?.forecastData?.forecastedNeeds ? (
                                <div className="flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[14px] text-r-50">
                                        warning
                                    </span>
                                    <span className="text-[#FF3D1D]">
                                        {millify(comp?.forecastData?.forecastedNeeds, {
                                            precision: 2,
                                        })}{" "}
                                        {comp?.productStockDetails?.unitOfMeasurement || ""}
                                    </span>
                                </div>
                            ) : (
                                <span className="text-[#56B648]">Sufficient</span>
                            )}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        <p className="">
                            {comp?.leadTime?.value ? (
                                <span className="">
                                    {comp?.leadTime?.value} {comp?.leadTime?.duration || "days"}
                                </span>
                            ) : comp?.forecastData?.leadTime ? (
                                <span className="">{comp?.forecastData?.leadTime} days</span>
                            ) : (
                                "Not Available"
                            )}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        <p className="">
                            {comp?.forecastData?.forecastedNeeds
                                ? `${"$ "}${millify(
                                      comp?.forecastData?.forecastedNeeds * comp?.unitCost?.amount,
                                      { precision: 2 }
                                  )}`
                                : "Not Available"}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },

            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        {!comp?.forecastData?.incidentType && !comp?.forecastData?.stockoutDate ? (
                            "Not Available"
                        ) : comp?.forecastData?.incidentType?.toLowerCase() !== "stockout" ? (
                            <span className=""> Overstocked </span>
                        ) : // comp?.forecastData?.incidentType?.toLowerCase() === "stockout" ||
                        differenceInDays > 0 && differenceInDays <= 7 ? (
                            <>
                                <span className="text-[#E4281D] block"> Immediately </span>
                                <span className={` text-slate-500 block`}>
                                    {moment().format("MMM DD, YYYY")}
                                </span>
                            </>
                        ) : differenceInDays < 0 ? (
                            <span className="text-[#E4281D]"> Stock out </span>
                        ) : (
                            <>
                                <span
                                    className={`block ${
                                        differenceInDays > 10
                                            ? "text-carribean-green"
                                            : "text-[#E4281D]"
                                    }`}
                                >
                                    {differenceInDays === 0
                                        ? "Today"
                                        : formatMoney().format(differenceInDays) + " days"}
                                </span>
                                <span className={`block text-slate-500`}>
                                    {comp?.forecastData?.stockoutDate
                                        ? moment(comp?.forecastData?.stockoutDate).format(
                                              "MMM DD, YYYY"
                                          )
                                        : ""}
                                </span>
                            </>
                        )}
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },
            {
                content: (
                    <div className={`w-full py-2`} onClick={(e) => e.stopPropagation()}>
                        <Dropdown
                            dropdown={<i className="ri-more-2-fill text-lg text-slate-500"></i>}
                            dropdownClassName={"!w-full !mb-10 !-mt-7"}
                            dropdownContainerClasses={`shadow-cardShadow-2 !z-[2000] !top-[-80px] border-0 rounded !max-h-fit right-[60px] `}
                            name={"dropdown"}
                            dropdownItemsClasses="!bg-transparent"
                            dropdownOptions={[
                                {
                                    label: "View component",
                                    value: "View component",
                                    action: () => {
                                        navigate(`/dashboard/inventory/products/${comp?._id}`, {
                                            state: { internalNavigation: true },
                                        });
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                                {
                                    label: "Restock component",
                                    value: "Restock component",
                                    action: () => {
                                        setGetProductId(comp?._id);
                                        navigate(
                                            `/dashboard/purchase/create-purchase-request?inventory=${comp?._id}`,
                                            { state: { internalNavigation: true } }
                                        );
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                                {
                                    label: "Adjust component",
                                    value: "Adjust component",
                                    action: () => {
                                        navigate(
                                            `/dashboard/inventory/stock-control/stock-adjustment?currTab=0&inventoryId=${comp?._id}&warehouseId=${comp?.productStockSettings[0]?.warehouseId}`
                                        );
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                            ]}
                        />
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },
        ];
    });

    return (
        <div>
            <div className="overflow-x-scroll mb-6">
                <CustomTable
                    tableBody={tableBody}
                    tableHeader={tableHeader}
                    isCellBordered={true}
                    isCellSelectable={true}
                    isAllSelectable={true}
                    stickyHeader={true}
                    isScrollable={true}
                    headerContainerClass="!bg-slate-50 !border-slate-200"
                    headerItemClass="font-rocGroteskMedium "
                    tableClass="mb-12 !border-slate-200"
                    isCollapseable={true}
                    // isCollapseableIconIndex={1}
                    expandedRowIdx={expandedRowIdx}
                    allCellClick={true}
                    scrollContentId="table-container-one"
                    rowExpanded={
                        <div className="relative z-[1300]">
                            <div className="px-3 pt-2.5 bg-slate-50">
                                <p className="text-xs text-slate-500 font-rocGroteskMedium">
                                    Forecast covers the next{` `}
                                    {selectedInvPeriod || "6 months"}. You can change this by using
                                    the forecast period filter
                                </p>
                            </div>
                            <div className="pt-2.5 px-3 bg-slate-50">
                                {componentList?.length > 0 ? (
                                    <div className="overflow-x-scroll">
                                        <CustomTable
                                            tableBody={expandedTableBody}
                                            tableHeader={expandedTableHeader}
                                            isCellBordered={true}
                                            stickyHeader={true}
                                            headerContainerClass="!bg-slate-50 !border-slate-200"
                                            headerItemClass="font-rocGroteskMedium "
                                            tableClass="mb-3 !border-t-slate-200 !border-x-slate-200"
                                            isScrollable={true}
                                            tableContainerClass=""
                                            bodyItemClass="!border-slate-200"
                                            expandedRowTClass="!z-[1300]"
                                            scrollWithin={false}
                                        />
                                    </div>
                                ) : (
                                    <div className="py-4 flex items-center justify-center ">
                                        <p className="text-slate-500 font-rocGroteskMedium text-sm">
                                            No Component available for this product
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    }
                    isLoading={productListLoading && data.length < 1}
                    renderEmptyTable={() => (
                        <EmptyStateComp
                            title={`No ${
                                starredFilter === "starred" ? "Starred" : ""
                            } Product Available `}
                            description={
                                starredFilter === "starred"
                                    ? "Click the star icon on any product to add it here for quick access."
                                    : "There are no products in your inventory. Add new products to start managing your inventory."
                            }
                            hasButton
                            btnText="Add new product"
                            btnAction={() =>
                                navigate("/dashboard/inventory/add-product", {
                                    state: { internalNavigation: true },
                                })
                            }
                            icon={<i className="ri-box-3-line text-slate-500 text-[40px]"></i>}
                        />
                    )}
                />

                {productListLoading && (
                    <div className="flex justify-center my-4">
                        <Loader color="gm-25" size={4} />
                    </div>
                )}

                {pagination?.currentPage === pagination?.noOfPages && (
                    <div className="flex my-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                        End of list
                    </div>
                )}
            </div>
        </div>
    );
};

export default VariantTable;
